import { h } from 'vue'
import { ElSwitch } from 'element-plus'

/**
 * el-switch 兼容包装（全局覆盖 Element Plus 的 ElSwitch）
 *
 * 背景：Element Plus 的 el-switch 在 setup 阶段会做一次值校验——
 *   if (![activeValue, inactiveValue].includes(modelValue)) { emit('update:modelValue', inactiveValue); emit('change', inactiveValue) }
 * 也就是说「初始值不属于 active/inactive 之一」时，会立刻伪造一次 change 事件。
 *
 * 触发场景（很常见）：el-table 的列模板会被渲染两次——真实行 + div.hidden-columns 里用空对象 {} 预渲染
 * （用于列宽测量）。空对象上 scope.row.status 是 undefined，于是每张表格在渲染时都会凭空触发一次
 * change，业务代码里的 @change="xxxStatus(row)" 就被调用了，参数是 {status:-10}（没有 id）。
 *
 * 处理：初始化阶段的这次伪造事件一律丢弃（它在子组件 setup 中同步触发，早于本包装组件的 mounted），
 * 用户在挂载之后的真实点击（含键盘）照常透传 change / update:modelValue，v-model 行为不变。
 */
export default {
  name: 'ElSwitchCompat',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [Boolean, String, Number],
      default: false
    }
  },
  emits: ['update:modelValue', 'change', 'input'],
  data() {
    return {
      // 首次挂载完成前为 false，用于丢弃 el-switch 初始化时的伪造事件
      ready: false
    }
  },
  mounted() {
    this.ready = true
  },
  render() {
    // active-value / inactive-value 经 $attrs 透传（模板里写的是 kebab-case）
    const activeValue = this.$attrs['active-value'] !== undefined ? this.$attrs['active-value'] : this.$attrs.activeValue
    const inactiveValue = this.$attrs['inactive-value'] !== undefined ? this.$attrs['inactive-value'] : this.$attrs.inactiveValue
    let modelValue = this.modelValue
    // 挂载前（el-table 隐藏列预渲染空行等场景）modelValue 不属于 active/inactive 之一时，
    // 直接以 inactiveValue 渲染。否则 EP 内部校验会 console.warn
    // "[ElSwitch] model-value must be active-value or inactive-value"，
    // 并伪造一次 change/update（本次渲染改为不触发）。挂载后的真实交互照常透传。
    if (!this.ready && ![activeValue, inactiveValue].includes(modelValue)) {
      modelValue = inactiveValue !== undefined ? inactiveValue : false
    }
    return h(
      ElSwitch,
      {
        ...this.$attrs,
        modelValue,
        'onUpdate:modelValue': (val) => {
          if (this.ready) this.$emit('update:modelValue', val)
        },
        onChange: (val) => {
          if (this.ready) this.$emit('change', val)
        }
      },
      this.$slots
    )
  }
}
